import { FC, Dispatch, SetStateAction } from 'react';
import {
  Action,
  DeploymentUnitStatus,
  NotDeployedActions,
  WitboostSubcomponent,
} from '@agilelab/plugin-wb-builder-common';
import {
  TableContentLoader,
  WbSelect,
  WbTable,
} from '@agilelab/plugin-wb-platform';
import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';
import {
  Box,
  makeStyles,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { ComponentDependencies } from '../ProvisioningCard/ComponentsTable/ComponentDependencies';
import { DeploymentPreview } from '../types';
import { DeploymentPreviewChip } from './DeploymentPreviewChip';
import { SubComponentRow } from './SubComponentRow';

interface Props {
  rows: Array<DeploymentPreview>;
  loading: boolean;
  enableCleanUpMultiSelection?: boolean;
  setCleanUpPreviews?: Dispatch<SetStateAction<DeploymentPreview[]>>;
}

const useStyles = makeStyles(theme => ({
  selectInput: {
    height: '36px',
    minHeight: 'auto',
    fontSize: theme.typography.body1.fontSize,
  },
  selectTextField: {
    minHeight: 0,
  },
}));

export const DeploymentPreviewTable: FC<Props> = ({
  rows,
  loading,
  enableCleanUpMultiSelection,
  setCleanUpPreviews,
}) => {
  const theme = useTheme();
  const headStyle = { style: { fontWeight: 500 } };
  const classes = useStyles();

  return (
    <WbTable
      stickyHeader
      components={{
        tableLoader: {
          loading: false,
        },
        tableHeader: (
          <TableHead>
            <TableRow>
              <TableCell {...headStyle}>Name</TableCell>
              <TableCell {...headStyle}>Type</TableCell>
              <TableCell {...headStyle}>Dependencies</TableCell>
              <TableCell {...headStyle}>Current State</TableCell>
              <TableCell {...headStyle}>New State</TableCell>
              <TableCell {...headStyle}>Action</TableCell>
            </TableRow>
          </TableHead>
        ),
        tableContent: {
          body: (
            <TableBody>
              {loading && <TableContentLoader rows={10} tableCells={6} />}

              {!loading &&
                rows.map((preview, i) => {
                  return (
                    <>
                      <TableRow key={i}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            style={{ fontWeight: 700 }}
                          >
                            {preview.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {snakeCaseToTitleCase(preview.kind)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <ComponentDependencies preview={preview} />
                        </TableCell>
                        <TableCell>
                          <Box
                            style={{
                              display: 'flex',
                              gap: '4px',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              color: theme.palette.accent.main,
                            }}
                          >
                            <DeploymentPreviewChip
                              status={preview.status}
                              version={preview.statusVersion}
                            />
                            <ArrowForwardIcon />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <DeploymentPreviewChip
                            status={
                              preview.newState as unknown as DeploymentUnitStatus
                            }
                          />
                        </TableCell>
                        {enableCleanUpMultiSelection ? (
                          <TableCell
                            style={{ maxWidth: 300, minWidth: 300, width: 300 }}
                          >
                            <WbSelect
                              ariaLabel="action"
                              value={preview.action}
                              className={classes.selectTextField}
                              onChange={e => {
                                const value = e.target.value;
                                preview.action = value as Action;
                                setCleanUpPreviews?.(previews => {
                                  const indexPreview = previews.findIndex(
                                    p => p.id === preview.id,
                                  );
                                  if (indexPreview !== -1) {
                                    previews[indexPreview] = preview;
                                    return [...previews];
                                  }
                                  return [...previews, preview];
                                });
                              }}
                              options={
                                !preview.action ||
                                NotDeployedActions.includes(preview.action)
                                  ? NotDeployedActions
                                  : [preview.action]
                              }
                              getOptionLabel={value =>
                                snakeCaseToTitleCase(value)
                              }
                              InputProps={{
                                className: classes.selectInput,
                              }}
                            />
                          </TableCell>
                        ) : (
                          <TableCell>
                            <Typography
                              variant="body2"
                              style={{ fontWeight: 700 }}
                            >
                              {preview.action &&
                                snakeCaseToTitleCase(preview.action)}
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>

                      {(preview.components || []).map(
                        (subComponent: WitboostSubcomponent) => (
                          <SubComponentRow
                            key={subComponent.id}
                            subComponent={subComponent}
                            componentPreview={preview}
                          />
                        ),
                      )}
                    </>
                  );
                })}
            </TableBody>
          ),
        },
      }}
      styles={{
        container: { height: '100%' },
      }}
    />
  );
};
